import { MessageSquare, CheckCircle, Clock, Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { getSessionNotes, deleteNote } from "@/lib/collections/datapicker/getSessionsAndBreaks";
import { toast } from "react-toastify";

type Note = {
    $id: string;
    userId: string;
    sessionId: string;
    noteContent: string;
    createdAt: string;
    updatedAt?: string;
    isRead: boolean;
};

type NotesDialogProps = {
    sessionId: string;
    userName: string;
    selectedDate: Date;
};

const NotesDialog = ({ sessionId, userName, selectedDate }: NotesDialogProps) => {
    const [notes, setNotes] = useState<Note[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [notesCount, setNotesCount] = useState<number | null>(null);
    const [deletingNotes, setDeletingNotes] = useState<Set<string>>(new Set());

    //cargar las notas cuando se abre el dialog
    const loadNotes = async () => {
        if (hasLoaded) return;

        setLoading(true);
        try {
            const fetchedNotes = await getSessionNotes(sessionId);
            setNotes(fetchedNotes);
            setNotesCount(fetchedNotes.length);
            setHasLoaded(true);
        } catch (error) {
            console.error("Error loading notes:", error);
            setNotes([]);
            setNotesCount(0);
        } finally {
            setLoading(false);
        }
    };

    //para verificar si la fecha es  de hoy
    const isToday = (date: Date) => {
        const today = new Date();
        // Configurar timezone para Ecuador (GMT-5)
        const ecuadorToday = new Date(today.toLocaleString("en-US", { timeZone: "America/Guayaquil" }));
        const selectedDateEcuador = new Date(date.toLocaleString("en-US", { timeZone: "America/Guayaquil" }));
        
        return ecuadorToday.toDateString() === selectedDateEcuador.toDateString();
    };

    //  eliminar una nota
    const handleDeleteNote = async (noteId: string) => {
        // Verificar si la fecha seleccionada es hoy
        if (!isToday(selectedDate)) {
            toast.error("Solo puedes eliminar notas del día actual");
            return;
        }

        // Prevenir varios clicks
        if (deletingNotes.has(noteId)) {
            return;
        }

        // Agregar el noteId al set de notas siendo eliminadas
        setDeletingNotes(prev => new Set(prev).add(noteId));

        try {
            await deleteNote(noteId);
            
            // Actualizar el estado local
            setNotes(prevNotes => {
                const updatedNotes = prevNotes.filter(note => note.$id !== noteId);
                setNotesCount(updatedNotes.length);
                return updatedNotes;
            });

            // mostrar notificacion de exito
            toast.success("Nota eliminada correctamente");
            
        } catch (error) {
            console.error("Error deleting note:", error);
            toast.error("Error al eliminar la nota");
        } finally {
            // remover el noteId del set de notas siendo eliminadas
            setDeletingNotes(prev => {
                const newSet = new Set(prev);
                newSet.delete(noteId);
                return newSet;
            });
        }
    };

    // cargar conteo inicial sin mostrar nada hasta que sepamos si hay notas
    useEffect(() => {
        if (notesCount === null) {
            getSessionNotes(sessionId).then(fetchedNotes => {
                setNotesCount(fetchedNotes.length);
                // si no hay notas, marcar como cargado para evitar mas requests
                if (fetchedNotes.length === 0) {
                    setHasLoaded(true);
                }
            }).catch(() => {
                setNotesCount(0);
                setHasLoaded(true);
            });
        }
    }, [sessionId]);

    // Si aun estamos cargando el conteo inicial, no mostrar nada
    if (notesCount === null) return null;

    // Si no hay notas, no mostrar el boton
    if (notesCount === 0) return null;

    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button
                    variant="outline"
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={loadNotes}
                >
                    <MessageSquare className="w-4 h-4" />
                    Notas {notesCount !== null && `(${notesCount})`}
                </Button>
            </DialogTrigger>

            <DialogContent className="max-w-2xl max-h-[70vh] overflow-auto font-outfit">
                <DialogHeader>
                    <DialogTitle className="font-bold text-xl text-dark">
                        Notas de {userName}
                    </DialogTitle>
                    <p className="text-sm text-gray-500">
                        Fecha: {selectedDate.toLocaleDateString("es-ES", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                        })}
                    </p>
                </DialogHeader>

                <div className="mt-4 space-y-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin" />
                            <span className="ml-2">Cargando notas...</span>
                        </div>
                    ) : notes.length > 0 ? (
                        notes.map((note) => (
                            <div
                                key={note.$id}
                                className={`p-4 border rounded-lg font-nunito ${note.isRead
                                        ? 'bg-green-50 border-green-200 dark:border-green-200 dark:bg-black'
                                        : 'bg-blue-50 border-blue-200  dark:border-blue-300 dark:bg-black'
                                    }`}
                            >
                                <div className="flex justify-between items-start gap-3">
                                    <div className="flex gap-2 flex-1">
                                        <div className="flex-shrink-0 mt-1">
                                            {note.isRead ? (
                                                <CheckCircle className="w-5 h-5 text-green-500" />
                                            ) : (
                                                <Clock className="w-5 h-5 text-blue-500" />
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <p className="break-words text-gray-800 dark:text-white mb-2">
                                                {note.noteContent}
                                            </p>
                                            <div className="flex items-center gap-4 text-xs ">
                                                <span className="text-blue-500">
                                                    Enviado: {new Date(note.createdAt).toLocaleString("es-ES", {
                                                        timeZone: "America/Guayaquil",
                                                        year: "numeric",
                                                        month: "short",
                                                        day: "numeric",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                </span>
                                                {note.updatedAt && note.updatedAt !== note.createdAt && (
                                                    <span className="text-green-600">
                                                        Leído: {new Date(note.updatedAt).toLocaleString("es-ES", {
                                                            timeZone: "America/Guayaquil",
                                                            year: "numeric",
                                                            month: "short",
                                                            day: "numeric",
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                        })}
                                                    </span>
                                                )}
                                                <span className={`px-2 py-1 rounded-full text-xs ${note.isRead
                                                        ? 'bg-green-100 text-green-600 '
                                                        : 'bg-blue-100 text-blue-600'
                                                    }`}>
                                                    {note.isRead ? 'Leído' : 'No leído'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Botón de eliminar - Solo mostrar si es el día de hoy */}
                                    {isToday(selectedDate) && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="flex-shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50 p-2 cursor-pointer"
                                            onClick={() => handleDeleteNote(note.$id)}
                                            disabled={deletingNotes.has(note.$id)}
                                        >
                                            {deletingNotes.has(note.$id) ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Trash2 className="w-4 h-4" />
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-center text-gray-500 py-8">
                            No hay notas para esta sesión
                        </p>
                    )}
                </div>

                <div className="mt-6 flex justify-end">
                    <DialogClose asChild>
                        <Button variant="default" className="cursor-pointer">
                            Cerrar
                        </Button>
                    </DialogClose>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default NotesDialog;